import { FC, Suspense, lazy, memo, useCallback } from "react";

import LoadingComponent from "../../../../components/LoadingComponent";
import useAppNavigate from "../../../../hooks/router/useAppNavigate";
import { PortfolioTab, paths } from "../../../../pages/routes";
import { useAppContext } from "../../../../stores";
import { useZapContext } from "../../../../stores/LiquidityContext/ZapContext/context";
import {
  ELiquidityFlowState,
  useLiquidityContext,
} from "../../../../stores/LiquidityContext/context";
import SubmittingZapTransaction from "../SubmittingZapTransaction/SubmittingZapTransaction";
import ZapSetup from "../ZapSetup";

const TransactionSubmittingWrapper = lazy(
  () =>
    import(
      /* webpackChunkName: "TransactionSubmittingWrapper" */ "../../../../components/TransactionSubmittingWrapper"
    )
);
const LazyTransactionError = lazy(
  () =>
    import(
      /* webpackChunkName: "TransactionError" */ "../../../../components/OrderComponents/OrderError"
    )
);
const LazyTransactionSuccess = lazy(
  () =>
    import(
      /* webpackChunkName: "TransactionSuccess" */ "../../../../components/OrderComponents/OrderSuccess"
    )
);

const ZapFlow: FC = () => {
  const navigate = useAppNavigate();
  const {
    state: { liquidityFlowState },
  } = useLiquidityContext();
  const { dispatch } = useAppContext();
  const {
    handlers: { handleCloseDialog },
  } = useZapContext();

  const onZapSuccess = useCallback(() => {
    navigate({
      to: paths.root,
      search: {
        tab: PortfolioTab.orders,
      },
    });
    handleCloseDialog();
  }, [dispatch, navigate, handleCloseDialog]);

  /* -------------------------------------------------------------------------------------------------
   * Initial Pool Selection / Asset Amount
   * -----------------------------------------------------------------------------------------------*/
  if (
    [
      ELiquidityFlowState.initial,
      ELiquidityFlowState.previewCancelled,
      ELiquidityFlowState.reset,
      ELiquidityFlowState.preview,
      ELiquidityFlowState.submittingCancelled,
    ].includes(liquidityFlowState)
  ) {
    return <ZapSetup />;
  }

  /* -------------------------------------------------------------------------------------------------
   * Submitting UI
   * -----------------------------------------------------------------------------------------------*/
  if (liquidityFlowState === ELiquidityFlowState.submitting) {
    return (
      <Suspense fallback={<LoadingComponent size="medium" />}>
        <TransactionSubmittingWrapper className="mt-4">
          <SubmittingZapTransaction />
        </TransactionSubmittingWrapper>
      </Suspense>
    );
  }

  /* -------------------------------------------------------------------------------------------------
   * Order Status (Error) UI
   * -----------------------------------------------------------------------------------------------*/
  if (ELiquidityFlowState.error === liquidityFlowState) {
    return (
      <Suspense fallback={<LoadingComponent size="medium" />}>
        <LazyTransactionError />
      </Suspense>
    );
  }

  /* -------------------------------------------------------------------------------------------------
   * Order Status (Success) UI
   * -----------------------------------------------------------------------------------------------*/
  if (ELiquidityFlowState.success === liquidityFlowState) {
    return (
      <Suspense fallback={<LoadingComponent size="medium" />}>
        <LazyTransactionSuccess
          className="mb-4"
          onCancel={handleCloseDialog}
          onSuccess={onZapSuccess}
        />
      </Suspense>
    );
  }

  return null;
};

export default memo(ZapFlow);
